/**
 * useTutorLiveCenterReviews — les avis du masteur sur l'activité au centre, et « Signaler » une question
 * du quiz.
 *
 *  - **La correction de l'IA** (`TodoModal:212-290`) : l'avis déjà donné est relu
 *    (`getActivityCorrectionFeedback`) quand la correction est finie (`correction_status = completed`),
 *    remis à vide sinon ; « Oui » / « À revoir » → `submitActivityCorrectionFeedback({ activityId,
 *    feedbackType })`, gelé pendant l'envoi, modifiable ensuite ; un échec se dit.
 *  - **Le quiz de prérequis** (`TodoModal:680-716`) : prérempli de `tutor_feedback`, donné **une seule
 *    fois** ; mesure `quiz.tutor.feedback.submit` (`quiz_id`, `activity_id`, `feedback_value`) puis
 *    `giveQuizFeedback({ activityId }, { feedback_value: 1 | -1 })`.
 *  - **« Signaler »** (`QuizQuestionSignalButton`, `context: "review"`) : la machine du signalement du
 *    moteur (`quizSignalReducer`), mesures `quiz.question.signal.button_click` et `…submitted`, puis
 *    `signalQuizQuestion({ activityId }, { quiz_question_id, signal_type, context, comment })` ; l'accusé
 *    de l'ancien (son texte, au vouvoiement).
 *
 * Chaque pouce mesure aussi `checkpoint.feedback.button_click` (l'ancien `FeedbackButton`, C-94).
 */
import * as amplitude from "@amplitude/analytics-react-native"
import { useCallback, useEffect, useReducer, useRef, useState } from "react"
import { useDispatch } from "react-redux"

import { activitiesService } from "../../../../../../data/services/activitiesService"
import {
    INITIAL_QUIZ_SIGNAL,
    quizSignalReducer,
    type QuizSignalType,
} from "../../../../../../engine/activity/prerequisite/quizQuestionSignal"
import { apiCall, apiFailureOf, type ApiService } from "../../../../../../engine/shared/apiCall"
import { type QuizQuestionSignalSheetProps } from "../../../../../../design-system/organisms/QuizQuestionSignalSheet/QuizQuestionSignalSheet"
import { apiErrorMessage } from "../../../hooks/apiErrorMessage"
import { type UICorrectionFeedback } from "../../../types/studentActivity"
import { liveQuizTutorFeedback, type ApiLiveActivity } from "../../../../adapters/tutor/Live/tutorLiveActivityAdapter"
import { quizFeedbackOf, quizQuestionIdAt } from "../../../../view-models/tutor/Live/tutorLiveCenterViewModel"
import { type CenterNotify } from "./useTutorLiveCenterActions"

const SIGNAL_CONTEXT = "review"

export const REVIEW_TEXTS = {
    /** L'accusé de l'ancien `QuizQuestionSignalButton`, mot pour mot. */
    signalSent: "Merci pour votre réponse. Pour ne pas rester bloqué, merci de tout de même choisir une réponse",
    feedbackFailed: "Ton avis n'est pas parti. Réessaie dans un instant.",
}

export type TutorLiveCenterReviews = {
    correctionReview: UICorrectionFeedback | null
    onCorrectionReview: (review: UICorrectionFeedback) => void
    quizFeedback: UICorrectionFeedback | null
    onQuizFeedback: (feedback: UICorrectionFeedback) => void
    onSignalQuestion: (index: number) => void
    quizSignal: QuizQuestionSignalSheetProps | null
}

export function useTutorLiveCenterReviews(raw: ApiLiveActivity | null, notify: CenterNotify): TutorLiveCenterReviews {
    const dispatch = useDispatch() as unknown as (action: never) => unknown
    const call = useCallback(
        <T = unknown>(service: unknown, params: Parameters<typeof apiCall>[2] = {}) =>
            apiCall<T>(dispatch, service as ApiService, params),
        [dispatch]
    )
    const activityId = raw?.id ?? null
    const notifyRef = useRef(notify)
    notifyRef.current = notify

    // ── La correction de l'IA ──────────────────────────────────────────────────────────────────────
    const [correctionReview, setCorrectionReview] = useState<UICorrectionFeedback | null>(null)
    const sendingReviewRef = useRef(false)
    const completed = raw?.correction_status === "completed"
    useEffect(() => {
        if (activityId == null || !completed) {
            setCorrectionReview(null)
            return
        }
        let current = true
        call<{ feedback_type?: string | null }>(activitiesService.getActivityCorrectionFeedback, {
            routeParams: { activityId },
        })
            .then((answer) => {
                const type = answer?.feedback_type
                if (current) setCorrectionReview(type === "positive" || type === "negative" ? type : null)
            })
            .catch(() => undefined)
        return () => {
            current = false
        }
    }, [activityId, completed, call])

    const onCorrectionReview = useCallback(
        (review: UICorrectionFeedback) => {
            if (activityId == null || sendingReviewRef.current) return
            amplitude.track("checkpoint.feedback.button_click")
            sendingReviewRef.current = true
            setCorrectionReview(review)
            call(activitiesService.submitActivityCorrectionFeedback, {
                data: { activityId, feedbackType: review },
            })
                .catch((cause) =>
                    notifyRef.current(apiErrorMessage(apiFailureOf(cause), REVIEW_TEXTS.feedbackFailed), "danger")
                )
                .finally(() => {
                    sendingReviewRef.current = false
                })
        },
        [activityId, call]
    )

    // ── L'avis sur le quiz ─────────────────────────────────────────────────────────────────────────
    const served = liveQuizTutorFeedback(raw)
    const [quizValue, setQuizValue] = useState<1 | -1 | null>(served)
    useEffect(() => {
        setQuizValue(served)
    }, [served, activityId])

    const onQuizFeedback = useCallback(
        (feedback: UICorrectionFeedback) => {
            // Une seule fois : l'avis donné ne se change plus (`handleQuizFeedback`).
            if (quizValue !== null || activityId == null) return
            const value = feedback === "positive" ? 1 : -1
            amplitude.track("checkpoint.feedback.button_click")
            amplitude.track("quiz.tutor.feedback.submit", {
                quiz_id: raw?.prerequisite_quiz?.id,
                activity_id: activityId,
                feedback_value: value,
            })
            setQuizValue(value)
            call<{ tutor_feedback?: number | null }>(activitiesService.giveQuizFeedback, {
                routeParams: { activityId },
                data: { feedback_value: value },
            })
                .then((answer) => {
                    const given = answer?.tutor_feedback
                    if (given === 1 || given === -1) setQuizValue(given)
                })
                .catch((cause) =>
                    notifyRef.current(apiErrorMessage(apiFailureOf(cause), REVIEW_TEXTS.feedbackFailed), "danger")
                )
        },
        [activityId, call, quizValue, raw?.prerequisite_quiz?.id]
    )

    // ── « Signaler » une question ──────────────────────────────────────────────────────────────────
    const [signal, dispatchSignal] = useReducer(quizSignalReducer, INITIAL_QUIZ_SIGNAL)
    const [questionId, setQuestionId] = useState<number | null>(null)

    const onSignalQuestion = useCallback(
        (index: number) => {
            const id = quizQuestionIdAt(raw, index)
            amplitude.track("quiz.question.signal.button_click", { context: SIGNAL_CONTEXT, question_id: id })
            setQuestionId(id)
            dispatchSignal({ type: "opened" })
        },
        [raw]
    )

    const sendSignal = useCallback(() => {
        if (signal.type == null || signal.sending || activityId == null || questionId == null) return
        const { type, comment } = signal
        dispatchSignal({ type: "sendStarted" })
        call(activitiesService.signalQuizQuestion, {
            routeParams: { activityId },
            data: { quiz_question_id: questionId, signal_type: type, context: SIGNAL_CONTEXT, comment },
        })
            .then(() => {
                amplitude.track("quiz.question.signal.submitted", {
                    signal_type: type,
                    context: SIGNAL_CONTEXT,
                    question_id: questionId,
                    comment,
                })
                dispatchSignal({ type: "sent" })
                notifyRef.current(REVIEW_TEXTS.signalSent, "success")
            })
            .catch(() => dispatchSignal({ type: "sendFailed" }))
    }, [activityId, call, questionId, signal])

    // Une autre activité : le signalement ouvert se ferme.
    useEffect(() => {
        dispatchSignal({ type: "closed" })
    }, [activityId])

    const quizSignal: QuizQuestionSignalSheetProps | null = signal.open
        ? {
              visible: true,
              type: signal.type,
              comment: signal.comment,
              sending: signal.sending,
              failed: signal.failed,
              onType: (type) => dispatchSignal({ type: "typeChosen", signal: type as QuizSignalType }),
              onComment: (comment) => dispatchSignal({ type: "commentChanged", comment }),
              onSend: sendSignal,
              onClose: () => dispatchSignal({ type: "closed" }),
          }
        : null

    return {
        correctionReview,
        onCorrectionReview,
        quizFeedback: quizFeedbackOf(quizValue),
        onQuizFeedback,
        onSignalQuestion,
        quizSignal,
    }
}
